import { FC, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Edit } from '@mui/icons-material'
import { Box, Card, IconButton, TableContainer, Tooltip, Typography } from '@mui/material'

import { GRTable, GRTableColumn } from '../../../components/GRTable/GRTable'
import usePage from '../../../hooks/usePage'
import pageService from '../../../services/PageService'
import { useGetInternalConventionalItemsQuery, useSaveConventionalItemMutation } from '../../api/conventional-genres'
import { useGetLanguageMapsQuery } from '../../api/language'
import { QueryStateBoundary } from '../../components/QueryStateBoundary/QueryStateBoundary'
import { ConventionalItemEditDialog } from '../../features/conventional-genres/components/ConventionalItemEditDialog/ConventionalItemEditDialog'
import {
  buildConventionalRows,
  ConventionalItemRow,
  conventionalFieldLabelKeys,
  conventionalTypeLabelKeys,
  getUnstoredConventionalFields,
} from '../../features/conventional-genres/helpers/conventionalGenreHelpers'
import { ConventionalItemMutation, ConventionalItemType } from '../../features/conventional-genres/types/InternalConventionalItem'
import { toSafeHttpUrl } from '../../helpers/safeUrl'
import { useMutationFeedback } from '../../hooks/useMutationFeedback'

type CustomProps = { onEdit: (row: ConventionalItemRow) => void }

export const ConventionalGenresPage: FC = () => {
  usePage(pageService.getInternalPageWithId('internal-conventional-genres'))
  const { t } = useTranslation()
  const { notifySuccess, notifyError } = useMutationFeedback()
  const categoriesQuery = useGetInternalConventionalItemsQuery(ConventionalItemType.Category)
  const genresQuery = useGetInternalConventionalItemsQuery(ConventionalItemType.Genre)
  const subgenresQuery = useGetInternalConventionalItemsQuery(ConventionalItemType.Subgenre)
  const languageMapsQuery = useGetLanguageMapsQuery()
  const [saveConventionalItem, { isLoading: isSaving }] = useSaveConventionalItemMutation()
  const [editedRow, setEditedRow] = useState<ConventionalItemRow>()

  const queries = [categoriesQuery, genresQuery, subgenresQuery, languageMapsQuery]
  const isLoading = queries.some((query) => query.isLoading)
  const isError = queries.some((query) => query.isError)
  const rows = useMemo(
    () =>
      buildConventionalRows({
        [ConventionalItemType.Category]: categoriesQuery.data,
        [ConventionalItemType.Genre]: genresQuery.data,
        [ConventionalItemType.Subgenre]: subgenresQuery.data,
      }),
    [categoriesQuery.data, genresQuery.data, subgenresQuery.data]
  )

  const tableColumns: GRTableColumn<ConventionalItemRow, CustomProps>[] = useMemo(
    () => [
      {
        labelAccessor: t('internal-service-management:conventional_genres_name'),
        accessor: ({ row }) => <Typography fontWeight="bold">{row.item.name || '---'}</Typography>,
        sortable: true,
        sortAccessor: ({ row }) => row.item.name?.toLowerCase() ?? '',
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 180 } },
      },
      {
        labelAccessor: t('internal-service-management:conventional_genres_description'),
        accessor: ({ row }) => <Typography variant="body2">{row.item.description}</Typography>,
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 320 } },
      },
      {
        labelAccessor: t('internal-service-management:conventional_genres_image'),
        accessor: ({ row }) => {
          const imageUrl = toSafeHttpUrl(row.item.imageUrl)
          return imageUrl ? (
            <Box textAlign="center">
              <a href={imageUrl} target="_blank" rel="noreferrer">
                <Box component="img" src={imageUrl} alt={row.item.imageText ?? row.item.name} sx={{ height: 60, maxWidth: '100%' }} />
              </a>
              <Typography variant="caption" display="block" fontWeight="bold">
                {row.item.imageText}
              </Typography>
            </Box>
          ) : null
        },
        headerCellProps: { sx: { minWidth: 140 } },
      },
      {
        labelAccessor: t('internal-service-management:conventional_genres_games'),
        accessor: ({ row }) => (row.type === ConventionalItemType.Subgenre ? row.item.exampleAppIds?.length ?? 0 : ''),
        headerCellProps: { sx: { minWidth: 80 } },
      },
      {
        labelAccessor: t('internal-service-management:conventional_genres_type'),
        accessor: ({ row }) => t(conventionalTypeLabelKeys[row.type]),
        sortable: true,
        sortAccessor: ({ row }) => t(conventionalTypeLabelKeys[row.type]),
        headerCellProps: { sx: { minWidth: 110 } },
      },
      {
        labelAccessor: '',
        accessor: ({ row, customTableProps }) => (
          <Tooltip title={t('internal-service-management:conventional_genres_edit_short')}>
            <IconButton
              color="primary"
              aria-label={t('internal-service-management:conventional_genres_edit_short')}
              onClick={() => customTableProps?.onEdit(row)}
            >
              <Edit />
            </IconButton>
          </Tooltip>
        ),
        cellProps: { align: 'right' },
        headerCellProps: { sx: { width: 80, minWidth: 80 } },
      },
    ],
    [t]
  )
  const [columns, setColumns] = useState(tableColumns)
  const customProps = useMemo<CustomProps>(() => ({ onEdit: setEditedRow }), [])

  const handleSave = useCallback(
    async (item: ConventionalItemMutation) => {
      if (!editedRow) return
      try {
        const saved = await saveConventionalItem({ type: editedRow.type, item }).unwrap()
        const unstored = getUnstoredConventionalFields(editedRow.type, item, saved)
        if (unstored.length) {
          const fields = unstored.map((field) => t(conventionalFieldLabelKeys[field])).join(', ')
          notifyError(t('internal-service-management:conventional_genres_not_stored', { fields }))
          return
        }
        notifySuccess(t('internal-service-management:conventional_genres_saved', { name: item.name }))
        setEditedRow(undefined)
      } catch {
        notifyError(t('internal-service-management:conventional_genres_save_failed'))
      }
    },
    [editedRow, notifyError, notifySuccess, saveConventionalItem, t]
  )

  return (
    <Box>
      <QueryStateBoundary isLoading={isLoading} isError={isError} errorText={t('internal-service-management:conventional_genres_load_failed')}>
        <TableContainer component={Card}>
          <GRTable
            rows={rows}
            columns={columns}
            onColumnsUpdated={setColumns}
            rowIdKey={(row) => `${row.type}-${row.item.id}`}
            customProps={customProps}
            noRowsLabel={t('internal-common:no_rows')}
            striped
            hoverable
          />
        </TableContainer>
      </QueryStateBoundary>

      <ConventionalItemEditDialog
        row={editedRow}
        languageMaps={languageMapsQuery.data}
        isSaving={isSaving}
        onClose={() => setEditedRow(undefined)}
        onSave={handleSave}
      />
    </Box>
  )
}
